import { isNil } from 'lodash';
import { ReactNode, useContext, useEffect, useId, useMemo, useRef } from 'react';
import { getAnchorName } from '@shared/styles';
import { useBoolean } from '@shared/util/hooks';
import { MenuButton } from './MenuButton';
import { MenuContent } from './MenuContent';
import {
  MenuAlignment,
  MenuContext,
  MenuContextValue,
  ParentMenuContext,
  RootMenuContext,
} from './MenuContext';
import { MenuIconButton } from './MenuIconButton';
import { MenuItem, MenuRow } from './MenuItem';
import { MenuNote } from './MenuNote';
import { MenuOptions, SearchableOptions, SelectedMarker } from './MenuOptions';
import { MenuScrollable } from './MenuScrollable';
import { MenuSection } from './MenuSection';
import { MenuSeparator, MenuSpacer } from './MenuSeparator';
import { MenuTrigger } from './MenuTrigger';
import { SubmenuContent } from './SubmenuContent';
import { SubmenuTrigger } from './SubmenuTrigger';

/**
 * Menu (MenuRoot)
 *
 * Compound component that manages open/close state for a dropdown menu. Wrap a
 * trigger and content together:
 *
 * ```tsx
 * <Menu>
 *   <Menu.Trigger><button>Open</button></Menu.Trigger>
 *   <Menu.Content>
 *     <Menu.Item label="Action" onClick={...} />
 *   </Menu.Content>
 * </Menu>
 * ```
 *
 * For nested menus, place `<Submenu>` inside `<Menu.Content>`, with a
 * `<Menu.Item>` as the `<Submenu.Trigger>`:
 *
 * ```tsx
 * <Menu.Content>
 *   <Menu.Item label="Edit" onClick={...} />
 *   <Submenu>
 *     <Submenu.Trigger>
 *       <Menu.Item label="More options" />
 *     </Submenu.Trigger>
 *     <Submenu.Content>
 *       <Menu.Item label="Duplicate" onClick={...} />
 *     </Submenu.Content>
 *   </Submenu>
 * </Menu.Content>
 * ```
 */

export interface MenuProps {
  children?: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  isOpenByDefault?: boolean;
  anchorName?: string;
  align?: MenuAlignment;
  closeOnSelect?: boolean;
  lightDismiss?: boolean;
}

export function MenuRoot({
  children,
  isOpen: isOpenProp,
  onOpenChange,
  isOpenByDefault = false,
  anchorName: anchorNameProp,
  align = 'right',
  closeOnSelect = true,
  lightDismiss = true,
}: MenuProps) {
  const rawId = useId();
  const anchorName = anchorNameProp ?? getAnchorName('menu', rawId);

  const rootMenu = useContext(RootMenuContext);
  const parentMenu = useContext(MenuContext);

  const [isOpen, open, close, toggle, , setValue] = useBoolean(isOpenProp ?? false);

  // Open after mount when isOpenByDefault is true. Using a ref + effect (rather than
  // initializing useState with isOpenByDefault) ensures the parent popover's showPopover()
  // has already run before the child tries to open — otherwise the browser's auto-dismiss
  // logic closes the child when the parent popover subsequently calls showPopover().
  const isOpenByDefaultRef = useRef(isOpenByDefault);
  useEffect(() => {
    if (isOpenByDefaultRef.current) {
      open();
    }
  }, [open]);

  useEffect(() => {
    if (!isNil(isOpenProp)) {
      setValue(isOpenProp);
    }
  }, [isOpenProp, setValue]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const menu = useMemo<MenuContextValue>(
    () => ({ anchorName, isOpen, open, close, toggle, closeOnSelect, align, lightDismiss }),
    [anchorName, isOpen, open, close, toggle, closeOnSelect, align, lightDismiss],
  );

  return (
    <RootMenuContext.Provider value={rootMenu ?? menu}>
      <ParentMenuContext.Provider value={parentMenu}>
        <MenuContext.Provider value={menu}>{children}</MenuContext.Provider>
      </ParentMenuContext.Provider>
    </RootMenuContext.Provider>
  );
}

function SubmenuRoot(props: MenuProps) {
  return <MenuRoot {...props} />;
}

export const Menu = Object.assign(MenuRoot, {
  Trigger: MenuTrigger,
  Button: MenuButton,
  IconButton: MenuIconButton,
  Content: MenuContent,
  Scrollable: MenuScrollable,
  Row: MenuRow,
  SelectedMarker,
  Item: MenuItem,
  Options: MenuOptions,
  SearchableOptions,
  Section: MenuSection,
  Note: MenuNote,
  Separator: MenuSeparator,
  Spacer: MenuSpacer,
});

export const Submenu = Object.assign(SubmenuRoot, {
  Trigger: SubmenuTrigger,
  Content: SubmenuContent,
});
